import type { BroadcastEnv } from "src/types/broadcasts";
import type {
  ExchangeOperatorTokenResponse,
  SendPincodeResponse,
} from "src/types/operatorAuth";
import { iv3Request } from "./iv3Client";

const PINCODE_PATH = "/login/v2/operator/auth/email/pincode";
// Verifies the emailed PINCODE (sms_pincodes — what PINCODE_PATH creates) and
// mints an OPERATOR-scoped token (operator Access records → passes operator
// checks like CreateMobileNotification's AccessLevel700+).
// NOT `.../auth/email/token` (verifies a magic link, wrong table for pincodes)
// and NOT `.../token/client-portal` (client/company-scoped token).
const TOKEN_PATH = "/login/v2/operator/verify-pincode";
const REFRESH_PATH = "/login/v2/operator/refresh";
const TOKEN_ISSUER = "OP";

export async function sendOperatorPincode(
  email: string,
  env: BroadcastEnv,
): Promise<SendPincodeResponse> {
  return iv3Request<SendPincodeResponse>(env, PINCODE_PATH, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function exchangeOperatorToken(
  email: string,
  env: BroadcastEnv,
  requestId: string,
  verificationCode: string,
): Promise<string> {
  const data = await iv3Request<ExchangeOperatorTokenResponse>(
    env,
    TOKEN_PATH,
    {
      method: "POST",
      body: JSON.stringify({
        email,
        requestId,
        verificationCode,
        issuer: TOKEN_ISSUER,
      }),
    },
  );
  const token = data.body?.trim();
  if (!token) throw new Error("Token response did not include a body.");
  return token;
}

/**
 * Exchange the current token for a fresh one — no OTP required. Used by the
 * interceptor (on 401) and the background scheduler. Throws on any non-2xx so
 * callers can fall back to re-OTP.
 */
export async function refreshOperatorToken(
  token: string,
  env: BroadcastEnv,
): Promise<string> {
  const data = await iv3Request<ExchangeOperatorTokenResponse>(
    env,
    REFRESH_PATH,
    {
      method: "POST",
      body: JSON.stringify({ body: token }),
    },
  );
  const next = data.body?.trim();
  if (!next) throw new Error("Refresh response did not include a body.");
  return next;
}
